import axios from "axios";
import {
  SITENAMEALIAS,
  WEBSERVICE as WEBSERVICE_ROOT,
  ACCOUNTS_WEBSERVICE,
} from "./init";
import { getAuthToken, getAuthUuid } from "./service";

/* ============================
   KYC API BASE CONFIGURATION
   ============================ */

const getHeaders = () => {
  return {
    "Content-Type": "application/json",
    authorization: "BEARER " + getAuthToken(),
    UUID: getAuthUuid(),
  };
};

const getMultipartHeaders = () => {
  return {
    authorization: "BEARER " + getAuthToken(),
    UUID: getAuthUuid(),
    // Do NOT set Content-Type — axios auto-sets it with correct boundary for FormData
  };
};

const WEBSERVICE = WEBSERVICE_ROOT + "/kyc";

/* ============================
   AUTH / USER INFO
   ============================ */

/**
 * Get BitoHub user info (called first to get user uuid)
 * POST /bito-hub/users/getBitohubUserExchangeInfo
 * @returns {Promise} - { data: { success, data: { uuid, ... } } }
 */
export const getBitoHubUserInfo = () => {
  return axios({
    method: "POST",
    url:
      WEBSERVICE.replace("/kyc", "") +
      "/bito-hub/users/getBitohubUserExchangeInfo",
    headers: getHeaders(),
    data: { adminUser: localStorage.getItem(SITENAMEALIAS + "_merchant_id") },
  });
};

/* Function defination to get user bank details */
// export const getUserBankDetails = async (payload) => {
//   const response = await axios.post(`https://institutional-bo.paybito.com:8443/BrokerAdminApi/admin/GetUserBankDetails`, payload,
//     {
//       headers: getHeaders(),
//     }
//   );
//   return response;
// }

/* ============================
   INDIVIDUAL KYC API CALLS
   ============================ */

/**
 * Save user KYC data for a specific step (JSON payload, no files)
 * Called at steps 0 (address), 1 (DOB), 2 (employment)
 * POST /finance-hub/saveUserKycData?adminUser=xxx
 *
 * @param {Object} payload - Complete user data with step-specific fields + uuid
 * @returns {Promise}
 */
export const saveUserKycData = (payload) => {
  const adminUser = localStorage.getItem(SITENAMEALIAS + "_merchant_id");

  // Log the payload to verify all data is being sent
  console.log("saveUserKycData payload:", payload);

  return axios({
    method: "POST",
    url: WEBSERVICE + "/saveUserKycData",
    headers: getHeaders(),
    data: payload, // Send the complete payload with ALL user data
  });
};

/**
 * Final individual KYC submission (FormData with user JSON + ALL files)
 * Called ONCE at step 5 (photo verification) with all documents collected.
 * POST /finance-hub/addUserKYCDetails
 *
 * FormData fields:
 *   user        : JSON string { firstName, middleName, lastName, address, city, state, country, zip,
 *                   uuid, userDocsStatus:"0", dob (MM-DD-YYYY), ssn, birthPlace, industry, occupation,
 *                   sourcesOfFunds, txnVolumeId, annualIncomeId, netWorthId, empCategory, empType,
 *                   pep, bankingPartner, relationWithBank, accountPurpose, thirdParty, panNo }
 *   adminUser   : merchant_id string
 *   panFront    : FILE (optional, India only)
 *   addressProof: FILE
 *   idProofFront: FILE
 *   idProofBack : FILE (optional)
 *   userPhoto   : FILE
 *
 * @param {FormData} payload - FormData with user JSON and file attachments
 * @returns {Promise}
 */
export const addUserKYCDetails = (payload) => {
  return fetch(WEBSERVICE + "/addUserKYCDetails", {
    method: "POST",
    headers: {
      authorization: "BEARER " + getAuthToken(),
      UUID: getAuthUuid(),
      // Do NOT set Content-Type — browser auto-sets with boundary for FormData
    },
    body: payload,
  }).then((response) => {
    if (!response.ok) {
      throw new Error("HTTP error " + response.status);
    }
    return response.json().then((data) => ({ data })); // wrap to match axios response shape
  });
};

/**
 * Get user KYC details/status
 * POST /finance-hub/GetUserDetails?adminUser=xxx
 * Response: { error: { error_data: 0 }, userResult: { ...UserDetails } }
 * @param {Object} payload - { uuid }
 * @returns {Promise}
 */
export const getUserKycDetails = (payload) => {
  const adminUser = localStorage.getItem(SITENAMEALIAS + "_exchange_uuid");
  return axios({
    method: "POST",
    url: WEBSERVICE + "/GetUserDetails",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Mark KYC as finished (called after addUserKYCDetails succeeds)
 * POST /finance-hub/finishKyc?adminUser=xxx&uuid=yyy
 * @param {Object} params - { adminUser, uuid }
 * @returns {Promise}
 */
export const finishKyc = (params) => {
  const adminUser =
    params.adminUser || localStorage.getItem(SITENAMEALIAS + "_merchant_id");
  const uuid = params.uuid || "";
  return axios({
    method: "POST",
    url: WEBSERVICE + "/finishKyc?uuid=" + uuid,
    headers: getHeaders(),
    data: {},
  });
};

/* ============================
   ENTERPRISE KYC API CALLS
   ============================ */

/**
 * Save enterprise company information (JSON payload, no files)
 * POST /finance-hub/saveEnterpriseUserInfo?adminUser=xxx&uuid=yyy
 * @param {Object} payload - Company information object
 * @returns {Promise}
 */
export const saveEnterpriseUserInfo = (payload) => {
  const adminUser = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
  const uuid = localStorage.getItem(SITENAMEALIAS + "_exchange_uuid") || "";

  return axios({
    method: "POST",
    url:
      WEBSERVICE +
      "/saveEnterpriseUserInfo?adminUser=" +
      adminUser +
      "&uuid=" +
      uuid,
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Add enterprise details with all documents
 * POST /finance-hub/addUserEnterpriseDetails
 * @param {FormData} payload - FormData with enterpriseUser JSON and all document files
 * @returns {Promise}
 */
export const addUserEnterpriseDetails = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/addUserEnterpriseDetails",
    headers: getMultipartHeaders(),
    data: payload,
  });
};

/**
 * Add or update enterprise owner
 * POST /finance-hub/addEnterpriseOwner
 * @param {FormData} payload - FormData with owner JSON and document files
 * @returns {Promise}
 */
export const addEnterpriseOwner = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/addEnterpriseOwner",
    headers: getMultipartHeaders(),
    data: payload,
  });
};

/**
 * Delete enterprise owner
 * POST /finance-hub/deleteEnterpriseOwner
 * @param {Object} payload - { ownerUuid, enterpriseId, action: 'DELETE' }
 * @returns {Promise}
 */
export const deleteEnterpriseOwner = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/deleteEnterpriseOwner",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Get enterprise KYC details
 * POST /finance-hub/getEnterpriseKycDetails
 * @param {Object} payload - { uuid }
 * @returns {Promise}
 */
export const getEnterpriseKycDetails = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/getEnterpriseKycDetails",
    headers: getHeaders(),
    data: payload,
  });
};

/* ============================
   UTILITY / SHARED KYC CALLS
   ============================ */

export const getLocationData = () => {
  return axios({
    method: "GET",
    url: WEBSERVICE + "/getLocationData",
    headers: getHeaders(),
  });
};

/**
 * Get list of countries for dropdown
 * @returns {Promise} Promise with countries data
 */
export const getCountries = () => {
  return axios({
    method: "GET",
    url:
      ACCOUNTS_WEBSERVICE +
      "/api/home//getExchangeCountries/PAYB18022021121103",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

/**
 * Get scanner token for QR code generation
 * @param {Object} payload - Contains merchant_id/uuid
 * @returns {Promise} Promise with scanner token data
 */
export const getScannerToken = (payload) => {
  return axios({
    method: "GET",
    url: `${WEBSERVICE}/getScannerToken?uuid=${payload.uuid}`,
    headers: getHeaders(),
  });
};

/**
 * Get user photo from API (for QR code capture polling)
 * @param {Object} payload - Contains uuid/country for image URL
 * @returns {Promise} Promise with image data
 */
export const getUserPhoto = (payload) => {
  const imageUrl = `${WEBSERVICE}/${payload.uuid}/${
    payload.country || "US"
  }/file/user_photo.png`;

  return axios({
    method: "GET",
    url: imageUrl,
    headers: {
      authorization: "BEARER " + getAuthToken(),
    },
    responseType: "blob",
  });
};

/**
 * Save PAN card details for Indian users
 * @param {Object} payload - Contains merchant_id, panNumber, panCardPhoto
 * @returns {Promise} Promise with response
 */
export const savePanCardDetails = (payload) => {
  const formData = new FormData();
  formData.append("merchant_id", payload.merchant_id);
  formData.append("panNumber", payload.panNumber);
  if (payload.panCardPhoto) {
    formData.append("panCardPhoto", payload.panCardPhoto);
  }

  return axios({
    method: "POST",
    url: WEBSERVICE + "/savePanCardDetails",
    headers: getMultipartHeaders(),
    data: formData,
  });
};

/**
 * Check if KYC is already completed
 * @param {Object} payload - Contains merchant_id
 * @returns {Promise} Promise with KYC status
 */
export const checkKycStatus = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/checkKycStatus",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Upload document for specific step
 * @param {Object} payload - Contains merchant_id, step, document
 * @returns {Promise} Promise with response
 */
export const uploadDocument = (payload) => {
  const formData = new FormData();
  formData.append("merchant_id", payload.merchant_id);
  formData.append("step", payload.step);
  formData.append("document", payload.document);

  return axios({
    method: "POST",
    url: WEBSERVICE + "/uploadDocument",
    headers: getMultipartHeaders(),
    data: formData,
  });
};

/**
 * Get document upload URL/presigned URL for S3
 * @param {Object} payload - Contains merchant_id, documentType, fileName
 * @returns {Promise} Promise with upload URL
 */
export const getDocumentUploadUrl = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/getDocumentUploadUrl",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Upload document directly to S3 using presigned URL
 * @param {string} url - Presigned URL from getDocumentUploadUrl
 * @param {File} file - File to upload
 * @returns {Promise} Promise with upload result
 */
export const uploadToS3 = (url, file) => {
  return axios({
    method: "PUT",
    url: url,
    headers: {
      "Content-Type": file.type,
    },
    data: file,
  });
};

/**
 * Verify document after upload
 * @param {Object} payload - Contains merchant_id, documentId, status
 * @returns {Promise} Promise with verification result
 */
export const verifyDocument = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/verifyDocument",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Get KYC progress/completion percentage
 * @param {Object} payload - Contains merchant_id
 * @returns {Promise} Promise with progress data
 */
export const getKycProgress = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/getKycProgress",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Resubmit rejected KYC
 * @param {Object} payload - Contains merchant_id, step to resubmit
 * @returns {Promise} Promise with response
 */
export const resubmitKyc = (payload) => {
  return axios({
    method: "POST",
    url: WEBSERVICE + "/resubmitKyc",
    headers: getHeaders(),
    data: payload,
  });
};

/**
 * Get merchant category codes for business activity dropdown
 * @returns {Promise}
 */
export const getMerchantCategoryCodes = () => {
  return axios({
    method: "GET",
    url: WEBSERVICE + "/getMerchantCategoryCodes",
    headers: getHeaders(),
  });
};
